type AmbientRig = {
    context: AudioContext
    master: GainNode
    reverbSend: GainNode
    chimeTimer: number | undefined
    stopTimer: number | undefined
}

const MASTER_LEVEL = 0.3
const CHIME_NOTES = [196, 246.94, 293.66, 329.63, 392, 493.88]

let rig: AmbientRig | null = null
let wanted = false

const createImpulse = (context: AudioContext, seconds: number, decay: number): AudioBuffer => {
    const length = Math.floor(context.sampleRate * seconds)
    const impulse = context.createBuffer(2, length, context.sampleRate)

    for (let channel = 0; channel < 2; channel++) {
        const data = impulse.getChannelData(channel)

        for (let i = 0; i < length; i++) {
            data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay)
        }
    }

    return impulse
}

const createNoise = (context: AudioContext, seconds: number): AudioBuffer => {
    const length = Math.floor(context.sampleRate * seconds)
    const buffer = context.createBuffer(1, length, context.sampleRate)
    const data = buffer.getChannelData(0)
    let last = 0

    for (let i = 0; i < length; i++) {
        // Brown-ish noise: soft, low, like air moving in a dome.
        last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02
        data[i] = last * 3.2
    }

    return buffer
}

const scheduleChime = (current: AmbientRig): void => {
    const delay = 9000 + Math.random() * 9000

    current.chimeTimer = window.setTimeout(() => {
        if (!rig || rig !== current || current.context.state !== 'running') return

        const { context, reverbSend } = current
        const now = context.currentTime
        const note = CHIME_NOTES[Math.floor(Math.random() * CHIME_NOTES.length)]
        const body = context.createOscillator()
        const partial = context.createOscillator()
        const bodyGain = context.createGain()
        const partialGain = context.createGain()

        body.type = 'sine'
        body.frequency.value = note
        partial.type = 'sine'
        partial.frequency.value = note * 2.76

        bodyGain.gain.setValueAtTime(0.0001, now)
        bodyGain.gain.exponentialRampToValueAtTime(0.022, now + 2.4)
        bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 10)
        partialGain.gain.setValueAtTime(0.0001, now)
        partialGain.gain.exponentialRampToValueAtTime(0.005, now + 1.8)
        partialGain.gain.exponentialRampToValueAtTime(0.0001, now + 6)

        body.connect(bodyGain)
        partial.connect(partialGain)
        bodyGain.connect(reverbSend)
        partialGain.connect(reverbSend)

        body.start(now)
        partial.start(now)
        body.stop(now + 10.5)
        partial.stop(now + 6.5)

        scheduleChime(current)
    }, delay)
}

const buildRig = (): AmbientRig | null => {
    const Context = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

    if (!Context) return null

    const context = new Context()
    const master = context.createGain()
    const bus = context.createGain()
    const lowpass = context.createBiquadFilter()
    const dry = context.createGain()
    const reverb = context.createConvolver()
    const reverbSend = context.createGain()
    const wet = context.createGain()

    master.gain.value = 0
    lowpass.type = 'lowpass'
    lowpass.frequency.value = 820
    lowpass.Q.value = 0.4
    dry.gain.value = 0.55
    wet.gain.value = 0.7
    reverbSend.gain.value = 1
    reverb.buffer = createImpulse(context, 6, 2.4)

    bus.connect(lowpass)
    lowpass.connect(dry)
    lowpass.connect(reverbSend)
    reverbSend.connect(reverb)
    reverb.connect(wet)
    dry.connect(master)
    wet.connect(master)
    master.connect(context.destination)

    const drones = [
        { frequency: 55, level: 0.2, detune: -5 },
        { frequency: 55, level: 0.16, detune: 6 },
        { frequency: 82.41, level: 0.11, detune: 0 },
        { frequency: 110, level: 0.06, detune: 3 },
        { frequency: 164.81, level: 0.025, detune: -3 }
    ]

    drones.forEach(({ frequency, level, detune }) => {
        const oscillator = context.createOscillator()
        const gain = context.createGain()
        const lfo = context.createOscillator()
        const lfoDepth = context.createGain()

        oscillator.type = 'sine'
        oscillator.frequency.value = frequency
        oscillator.detune.value = detune
        gain.gain.value = level
        lfo.frequency.value = 0.025 + Math.random() * 0.05
        lfoDepth.gain.value = level * 0.45

        lfo.connect(lfoDepth)
        lfoDepth.connect(gain.gain)
        oscillator.connect(gain)
        gain.connect(bus)
        oscillator.start()
        lfo.start()
    })

    const noise = context.createBufferSource()
    const band = context.createBiquadFilter()
    const noiseGain = context.createGain()
    const sweep = context.createOscillator()
    const sweepDepth = context.createGain()

    noise.buffer = createNoise(context, 6)
    noise.loop = true
    band.type = 'bandpass'
    band.frequency.value = 420
    band.Q.value = 3
    noiseGain.gain.value = 0.05
    sweep.frequency.value = 0.018
    sweepDepth.gain.value = 160

    sweep.connect(sweepDepth)
    sweepDepth.connect(band.frequency)
    noise.connect(band)
    band.connect(noiseGain)
    noiseGain.connect(bus)
    noise.start()
    sweep.start()

    return {
        context,
        master,
        reverbSend,
        chimeTimer: undefined,
        stopTimer: undefined
    }
}

const fadeTo = (current: AmbientRig, level: number, seconds: number): void => {
    const now = current.context.currentTime
    const gain = current.master.gain

    gain.cancelScheduledValues(now)
    gain.setValueAtTime(gain.value, now)
    gain.linearRampToValueAtTime(level, now + seconds)
}

const handleVisibility = (): void => {
    if (!rig) return

    if (document.hidden) void rig.context.suspend()
    else if (wanted) void rig.context.resume()
}

export const startAmbient = async (): Promise<void> => {
    wanted = true

    if (!rig) {
        rig = buildRig()
        if (!rig) return
        document.addEventListener('visibilitychange', handleVisibility)
    }

    window.clearTimeout(rig.stopTimer)

    try {
        await rig.context.resume()
    }
    catch {
        return
    }

    if (!wanted || !rig) return

    fadeTo(rig, MASTER_LEVEL, 3.5)
    window.clearTimeout(rig.chimeTimer)
    scheduleChime(rig)
}

export const stopAmbient = (): void => {
    wanted = false

    if (!rig) return

    const current = rig

    window.clearTimeout(current.chimeTimer)
    fadeTo(current, 0, 1.4)
    window.clearTimeout(current.stopTimer)
    current.stopTimer = window.setTimeout(() => {
        if (!wanted) void current.context.suspend()
    }, 1600)
}
